<template>
  <teleport to="body">
    <div
      v-if="dialogValue"
      v-bind="attrs"
      class="bg-overlay/60 scroll fixed inset-0 top-1/2 left-1/2 z-50 h-full w-full -translate-x-1/2 -translate-y-1/2"
      :style="{ zIndex }"
      @pointerdown="onOverlayPointerDown"
      @pointerup="onOverlayPointerUp"
    >
      <div
        ref="dialogEl"
        tabindex="-1"
        class="absolute top-1/2 left-1/2 flex max-h-9/10 min-h-52 max-w-full min-w-52 -translate-x-1/2 -translate-y-1/2 flex-col rounded bg-white outline-none"
        :class="variantClasses.dialog"
        :style="[
          'box-shadow: 0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12)',
          styles,
        ]"
      >
        <header
          v-if="title || slots.title"
          class="flex items-center justify-between px-8"
          :class="variantClasses.header"
        >
          <h2 class="text-beige-90 text-heading-1">
            <slot v-if="slots.title" name="title" />
            <template v-else> {{ title }} </template>
          </h2>
        </header>

        <main
          class="max-h-full min-h-px grow overflow-auto overscroll-contain"
          :class="variantClasses.main"
        >
          <span v-if="message" class="text-beige-90 text-body"> {{ message }} </span>
          <slot v-else></slot>
        </main>

        <footer
          class="flex w-full justify-end gap-2 self-end justify-self-end px-8"
          :class="variantClasses.footer"
        >
          <slot name="footer">
            <RfButtonNew type="ghost" @click="close">
              <span class="px-4">{{ cancelText }}</span>
            </RfButtonNew>
            <RfButtonNew
              v-if="!hideConfirm"
              type="secondary"
              :disabled="disabled"
              :loading="loading"
              @click="confirm"
            >
              <span class="px-4">{{ confirmText }}</span>
            </RfButtonNew>
          </slot>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script>
// Module scope — shared across every RfDialog instance, so nested dialogs
// (a dialog opened on top of another) only respond to Escape for the topmost.
const openDialogStack = [];
</script>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, onUnmounted, ref, useAttrs, useSlots } from "vue";
import { useOverlayZIndex } from "@/utils/composables/useOverlayZIndex";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const slots = useSlots();
const emit = defineEmits(["close", "open", "confirm"]);
const props = defineProps({
  title: { type: [String, null], default: "Modal title" },
  message: { type: [String, null], default: null },
  confirmText: { type: String, default: "Confirm" },
  cancelText: { type: String, default: "Cancel" },
  width: { type: [String, Number, null], default: null },
  height: { type: [String, Number, null], default: null },
  disabled: { type: [String, Boolean], default: false },
  loading: { type: Boolean, default: false },
  variant: { type: String, default: "default" },
  closeOnClickOutside: { type: Boolean, default: false },
  hideConfirm: { type: Boolean, default: false },
  keepOpenOnConfirm: { type: Boolean, default: false },
});

const { zIndex } = useOverlayZIndex();

const VARIANTS = {
  default: {
    dialog: "gap-6 py-8",
    header: "",
    main: "px-8",
    footer: "",
  },
  outlined: {
    dialog: "",
    header: "border-beige-10 border-b border-solid py-6",
    main: "px-0",
    footer: "border-beige-10 border-t border-solid py-4",
  },
};

const variantClasses = computed(() => VARIANTS[props.variant] ?? VARIANTS.default);

const dialogValue = ref(false);
const dialogEl = ref(null);
const pointerDownTarget = ref(null);
const dialogId = Symbol("rf-dialog");

const onOverlayPointerDown = e => {
  pointerDownTarget.value = e.target;
};

// Close only when both press and release landed on the backdrop itself, so a
// drag-select that starts or ends inside the dialog doesn't dismiss it. Uses
// pointer events so touch behaves the same as mouse.
const onOverlayPointerUp = e => {
  const startedOnOverlay = pointerDownTarget.value === e.currentTarget;
  pointerDownTarget.value = null;
  if (startedOnOverlay && e.target === e.currentTarget && props.closeOnClickOutside) close();
};

const styles = computed(() =>
  ["height", "width"].reduce((style, prop) => {
    if (props[prop]) {
      if (typeof props[prop] === "number" || /^\d+$/.test(props[prop])) {
        style[prop] = `${props[prop]}px`;
      } else {
        style[prop] = props[prop];
      }
    }
    return style;
  }, {}),
);

useEventListener("keydown", event => {
  if (event.key === "Escape" && dialogValue.value && openDialogStack.at(-1) === dialogId) {
    close();
  }
});

const open = () => {
  dialogValue.value = true;
  if (!openDialogStack.includes(dialogId)) openDialogStack.push(dialogId);
  document.body.style.overflow = "hidden";
  emit("open");
  nextTick(() => dialogEl.value?.focus());
};

const close = () => {
  dialogValue.value = false;
  const index = openDialogStack.indexOf(dialogId);
  if (index !== -1) {
    openDialogStack.splice(index, 1);
    // Keep the body locked while any nested dialog is still on top of us.
    if (!openDialogStack.length) document.body.style.overflow = "";
  }
  emit("close");
};

onUnmounted(() => {
  const index = openDialogStack.indexOf(dialogId);
  if (index !== -1) {
    openDialogStack.splice(index, 1);
    if (!openDialogStack.length) document.body.style.overflow = "";
  }
});

const confirm = () => {
  emit("confirm");
  if (!props.keepOpenOnConfirm) close();
};

defineExpose({ open, close });
</script>
